// ════════════════════════════════════════════════════════════════════
// ⭐⭐ THE GREEN FLAMES — caP'n, 2026-09-25
// ────────────────────────────────────────────────────────────────────
// "greenish with purple/black core fire … that green fire with a purple/blackish
//  core is going to be important in the games moving forward so when I mention
//  green flames its this"
// ⚑ WHAT GREEN FLAMES ARE: green on the outside, a purple-to-black core inside.
//   The core is DARK, not glowing: normal alpha blending, never additive, or the
//   black would vanish. Zero new lights (pier law): the fire is its own paint.
// Self-contained (three + r3f only) so any IE title can copy this file whole.
//
//   <GreenFlameRing radius={3} height={0.9} />   a ring of low fire, rising
//   <PaintedSeal radius={3.2} />                  the red-paint evil seal on the floor
// ════════════════════════════════════════════════════════════════════
import { useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

export const GREEN_FLAME = { outer: "#5dff7a", rim: "#c8ffb0", core: "#3a0b4a", heart: "#07010a" };

const VERT = /* glsl */`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
// value noise that WRAPS around the ring (period P on x), so there is no seam
const FRAG = /* glsl */`
  uniform float uTime; uniform float uP; uniform float uSeed;
  uniform vec3 uOuter; uniform vec3 uRim; uniform vec3 uCore; uniform vec3 uHeart;
  varying vec2 vUv;
  float h(vec2 p, float P) { p.x = mod(p.x, P); return fract(sin(dot(p, vec2(127.1, 311.7)) + uSeed) * 43758.5453); }
  float n(vec2 p, float P) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h(i, P), h(i + vec2(1, 0), P), f.x), mix(h(i + vec2(0, 1), P), h(i + vec2(1, 1), P), f.x), f.y);
  }
  float fbm(vec2 p, float P) {
    float a = 0.5, s = 0.0;
    for (int k = 0; k < 4; k++) { s += a * n(p, P); p *= 2.0; P *= 2.0; a *= 0.5; }
    return s;
  }
  void main() {
    // ⚑ caP'n 2026-09-25: "whispier single small flames" — not a wall. The ring is cut
    // into uP cells; only some hold a flame, each a thin teardrop that sways and flickers.
    float y = vUv.y;
    float cell = floor(vUv.x * uP);
    float r = h(vec2(cell, 3.0), uP);
    if (r < 0.5) discard;                              // about half the cells are empty
    float u = fract(vUv.x * uP) - 0.5;
    float t = uTime * (0.8 + r * 0.6) + r * 40.0;
    float hgt = 0.45 + 0.45 * h(vec2(cell, 9.0), uP);  // each flame its own height
    hgt *= 0.8 + 0.2 * sin(t * 5.0) * sin(t * 2.3);    // flicker
    float yy = y / hgt;
    if (yy > 1.0) discard;
    float sway = (sin(t * 2.7 + yy * 5.0) * 0.12 + (n(vec2(cell * 3.0, yy * 3.0 - t * 2.0), 999.0) - 0.5) * 0.18) * yy;
    float w = 0.2 * sin(3.14159 * pow(yy, 0.6)) * (1.0 - yy * 0.4);   // round belly, needle tip
    float d = abs(u - sway);
    float tongue = (w - d) / max(w, 0.001);            // 1 at the flame's centre line, 0 at its edge
    if (tongue <= 0.0) discard;
    float body = smoothstep(0.0, 0.35, tongue);
    float core = smoothstep(0.35, 0.75, tongue) * (1.0 - smoothstep(0.5, 0.9, yy));
    vec3 c = mix(uOuter, uRim, (1.0 - body) * 0.5);
    c = mix(c, uCore, core);
    c = mix(c, uHeart, core * smoothstep(0.6, 0.95, tongue));
    float a = body * (1.0 - smoothstep(0.6, 1.0, yy)) * 0.9;
    gl_FragColor = vec4(c, a);
  }
`;

function flameMat(seed, P) {
  return new THREE.ShaderMaterial({
    vertexShader: VERT, fragmentShader: FRAG,
    uniforms: {
      uTime: { value: 0 }, uP: { value: P }, uSeed: { value: seed },
      uOuter: { value: new THREE.Color(GREEN_FLAME.outer) }, uRim: { value: new THREE.Color(GREEN_FLAME.rim) },
      uCore: { value: new THREE.Color(GREEN_FLAME.core) }, uHeart: { value: new THREE.Color(GREEN_FLAME.heart) },
    },
    transparent: true, depthWrite: false, side: THREE.DoubleSide, toneMapped: false,
  });
}

// A ring of small, separate green flames licking up round a circle. `height` is
// the TALLEST a flame gets; most are shorter.
export function GreenFlameRing({ radius = 3, height = 0.5, position = [0, 0, 0], speed = 1 }) {
  const P = Math.max(8, Math.round(radius * 12));
  const mats = useMemo(() => [flameMat(1.3, P)], [P]);
  useEffect(() => () => mats.forEach((m) => m.dispose()), [mats]);
  useFrame((st) => {
    const t = st.clock.elapsedTime * speed;
    mats[0].uniforms.uTime.value = t;
  });
  return (
    <group position={position}>
      <mesh position={[0, height / 2, 0]} material={mats[0]} renderOrder={3}>
        <cylinderGeometry args={[radius, radius, height, 128, 1, true]} />
      </mesh>
    </group>
  );
}

// ── the seal: red paint on the planks, brushed not printed ──────────────
const TEX = new Map();
function sealTex() {
  if (TEX.has("seal")) return TEX.get("seal");
  const S = 1024, cv = document.createElement("canvas"); cv.width = cv.height = S;
  const ctx = cv.getContext("2d"), c = S / 2;
  let seed = 0x5EA1;
  const R = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
  const RED = "#9e0f16";
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  // a brushed stroke: several passes with jitter, uneven weight, like paint
  const brush = (pts, w, closed) => {
    for (let pass = 0; pass < 3; pass++) {
      ctx.strokeStyle = RED; ctx.globalAlpha = 0.55 + R() * 0.35; ctx.lineWidth = w * (0.7 + R() * 0.5);
      ctx.beginPath();
      pts.forEach(([x, y], i) => { const jx = x + (R() - 0.5) * w * 0.35, jy = y + (R() - 0.5) * w * 0.35; i ? ctx.lineTo(jx, jy) : ctx.moveTo(jx, jy); });
      if (closed) ctx.closePath();
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  };
  const circle = (r, w) => brush(Array.from({ length: 120 }, (_, i) => { const a = (i / 120) * Math.PI * 2; return [c + Math.cos(a) * r, c + Math.sin(a) * r]; }), w, true);
  circle(480, 16); circle(430, 9); circle(300, 11); circle(90, 7);
  // an inverted seven-point star across the inner circle
  const star = []; for (let i = 0; i <= 7; i++) { const a = Math.PI / 2 + (i * 3 * Math.PI * 2) / 7; star.push([c + Math.cos(a) * 300, c + Math.sin(a) * 300]); }
  brush(star, 8, false);
  // crude runes in the band between the two outer rings
  for (let i = 0; i < 21; i++) {
    const a = (i / 21) * Math.PI * 2, rr = 455;
    const x = c + Math.cos(a) * rr, y = c + Math.sin(a) * rr;
    ctx.save(); ctx.translate(x, y); ctx.rotate(a + Math.PI / 2);
    const g = [];
    for (let k = 0; k < 3; k++) g.push([(R() - 0.5) * 22, (R() - 0.5) * 30]);
    brush(g, 5, false);
    brush([[-8, 12], [8, -12]], 4, false);
    ctx.restore();
  }
  // spokes from the star to the outer band, and a sigil at each point
  for (let i = 0; i < 7; i++) {
    const a = Math.PI / 2 + (i * Math.PI * 2) / 7;
    brush([[c + Math.cos(a) * 300, c + Math.sin(a) * 300], [c + Math.cos(a) * 430, c + Math.sin(a) * 430]], 6, false);
    ctx.save(); ctx.translate(c + Math.cos(a) * 365, c + Math.sin(a) * 365); ctx.rotate(a);
    brush([[-14, -14], [14, 14]], 5, false); brush([[-14, 14], [14, -14]], 5, false); brush([[0, -20], [0, 20]], 4, false);
    ctx.restore();
  }
  // splatter and runs, where the paint was thrown on too heavy
  for (let i = 0; i < 90; i++) {
    const a = R() * Math.PI * 2, rr = 80 + R() * 420, s = 2 + R() * R() * 16;
    ctx.fillStyle = RED; ctx.globalAlpha = 0.5 + R() * 0.5;
    ctx.beginPath(); ctx.arc(c + Math.cos(a) * rr, c + Math.sin(a) * rr, s, 0, Math.PI * 2); ctx.fill();
  }
  for (let i = 0; i < 18; i++) {
    const a = R() * Math.PI * 2, rr = 470 + R() * 10, x = c + Math.cos(a) * rr, y = c + Math.sin(a) * rr, L = 10 + R() * 30;
    brush([[x, y], [x + Math.cos(a) * L, y + Math.sin(a) * L]], 5 + R() * 4, false);
  }
  ctx.globalAlpha = 1;
  // worn: the planks show through the paint
  ctx.globalCompositeOperation = "destination-out";
  for (let i = 0; i < 1400; i++) { ctx.globalAlpha = R() * 0.5; ctx.fillRect(R() * S, R() * S, 1 + R() * 5, 1 + R() * 2); }
  ctx.globalCompositeOperation = "source-over"; ctx.globalAlpha = 1;
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  TEX.set("seal", t); return t;
}

export function PaintedSeal({ radius = 3.2, position = [0, 0, 0] }) {
  const mat = useMemo(() => new THREE.MeshStandardMaterial({
    map: sealTex(), transparent: true, depthWrite: false, roughness: 0.55,
    // wet-looking red that still reads under the pier's three night lights
    emissive: "#5a0508", emissiveMap: sealTex(), emissiveIntensity: 0.9,
    polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3,
  }), []);
  useEffect(() => () => mat.dispose(), [mat]);
  return (
    <mesh position={[position[0], (position[1] || 0) + 0.012, position[2]]} rotation={[-Math.PI / 2, 0, 0]} material={mat} renderOrder={2}>
      <planeGeometry args={[radius * 2, radius * 2]} />
    </mesh>
  );
}

// the seal with green flames rising round its rim
export function FlamingSeal({ radius = 3.2, position = [0, 0, 0], flameHeight = 0.5 }) {
  return (
    <group position={position}>
      <PaintedSeal radius={radius} />
      <GreenFlameRing radius={radius * 0.97} height={flameHeight} />
    </group>
  );
}
